import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../api/notifications';
import { sanitizeText } from '../../utils/sanitize';
import { timeAgo } from '../../utils/timeAgo';
import { notificationIcon } from './notificationIcons';

const badgeLabel = (count) => (count > 9 ? '9+' : String(count));

const NotificationBell = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 15000,
  });

  const { data: listData, isLoading } = useQuery({
    queryKey: ['notifications-dropdown'],
    queryFn: () => fetchNotifications({ limit: 20 }),
    enabled: open,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-dropdown'] });
    },
  });

  const markOne = useMutation({
    mutationFn: markNotificationRead,
  });

  useEffect(() => {
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const unread = countData?.count || 0;
  const items = listData?.data || [];

  const handleClick = async (n) => {
    if (!n.isRead) {
      await markOne.mutateAsync(n._id);
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-zinc-600 dark:text-zinc-400 transition hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:text-zinc-100"
        aria-label={t('common.notifications')}
      >
        <span className="text-xl">🔔</span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {badgeLabel(unread)}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xl">
          <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-3">
            <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{t('notifications.title')}</span>
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={!unread || markAll.isPending}
              className="text-xs text-glow-400 hover:text-glow-300 disabled:opacity-40"
            >
              {t('notifications.markAllRead')}
            </button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {isLoading && <p className="p-4 text-sm text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
            {!isLoading && items.length === 0 && (
              <p className="p-4 text-sm text-zinc-600 dark:text-zinc-500">{t('notifications.noNotificationsYet')}</p>
            )}
            {items.map((n) => (
              <button
                key={n._id}
                type="button"
                onClick={() => handleClick(n)}
                className={`flex w-full gap-3 border-b border-zinc-200/80 dark:border-zinc-800/80 px-4 py-3 text-left transition hover:bg-zinc-200/50 dark:bg-zinc-800/50 ${
                  !n.isRead ? 'border-l-2 border-l-blue-500' : 'border-l-2 border-l-transparent'
                }`}
              >
                <span className="text-lg">{notificationIcon(n.type)}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-zinc-800 dark:text-zinc-200">
                    {sanitizeText(n.title)}
                  </p>
                  <p className="truncate text-xs text-zinc-600 dark:text-zinc-500">{sanitizeText(n.message)}</p>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-500">{timeAgo(n.createdAt)}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-zinc-200 dark:border-zinc-800 p-3 text-center">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-sm text-glow-400 hover:text-glow-300"
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
